import React, { createContext, useContext, useState, useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { logInfo, logError } from '../services/logtail';
import CassetteLoader from '../components/CassetteLoader';
import { auth } from '../config/firebase';
import {
  signInWithPopup,
  signInAnonymously,
  linkWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import type { User as FirebaseUser, AuthCredential } from 'firebase/auth';
import type { FirebaseError } from 'firebase/app';
import { abandonAnonymousSession, logoutAnonymousSession, prepareGuestAbandon, checkExistingAccount, completeCloudChoice as completeCloudChoiceApi, upsertAuthUser } from '../services/anonymousAuthService';
import { startStorageOAuthRedirect, registerStorageAuthExpiredHandler, skipStorageLink, disconnectStorage, type StorageProvider, type MigrationStatus } from '../services/storageService';
import { redeemPromoCode } from '../services/promocodeService';
import ga from '../services/analytics';
import { getStoredUtm, clearStoredUtm } from '../utils/utm';
import { getMetaAttribution } from '../utils/metaAttribution';
import { relayMetaCapiEvent, createMetaEventId } from '../services/metaCapiRelay';
import { authStore, startAuthStore, fetchUserInfo, resolveIsAnonymous } from '../auth/authStore';
import { isEffectivelyStorageLinked } from '../utils/storageNavigation';

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  isAnonymous: boolean;
  needsCloudChoice: boolean;
  signInWithGoogle: () => void;
  signInWithApple: () => void;
  signInAnonymously: () => Promise<void>;
  linkWithGoogle: () => Promise<void>;
  linkWithApple: () => Promise<void>;
  completeCloudChoice: () => Promise<void>;
  logout: () => Promise<void>;
  loggingOut: boolean;
  accountConflict: { open: boolean; method: 'google' | 'apple'; hasStorage?: boolean } | null;
  confirmExistingAccountSignIn: () => Promise<void>;
  dismissAccountConflict: () => void;
  conflictBusy: boolean;
  userType: 'starter' | 'artist' | 'studio';
  setUserType: (type: 'starter' | 'artist' | 'studio') => void;
  promoCode?: string;
  promoExpiry?: Date;
  applyPromoCode: (code: string) => Promise<{ success: boolean; message: string }>;
  driveLinked: boolean;
  driveEmail: string | null;
  storageLinked: boolean;
  usingCassetteCloud: boolean;
  includesCassetteCloud: boolean;
  eligibleForCassetteProjectStorage: boolean;
  storageProvider: StorageProvider | '';
  storageEmail: string | null;
  migrationStatus: MigrationStatus;
  s3Ready: boolean;
  linkGoogleDrive: () => void;
  linkStorage: (provider: StorageProvider) => Promise<void>;
  skipStorage: () => Promise<void>;
  unlinkGoogleDrive: () => void;
  disconnectStorage: () => Promise<void>;
  isGDriveTokenExpired: () => boolean;
  refreshIdToken: () => Promise<string>;
  idToken: string | null;
  oauthInProgress: boolean;
  clearUtmAfterPurchase: () => void;
  refreshUserData: () => Promise<void>;
  isNewUser: boolean;
  clearNewUserSession: () => void;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  defaultTrackId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  // Ensure the single auth bootstrap is running (idempotent).
  startAuthStore();

  // Reactive auth/profile state from the single source of truth.
  const snap = useSyncExternalStore(authStore.subscribe, authStore.getSnapshot, authStore.getSnapshot);
  const loading = snap.status === 'initializing';

  // UI-only state that does not belong in the global auth snapshot.
  const [oauthInProgress, setOauthInProgress] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const loggingOutRef = useRef(false);
  const [conflictBusy, setConflictBusy] = useState(false);
  const [accountConflict, setAccountConflict] = useState<{ open: boolean; method: 'google' | 'apple'; hasStorage?: boolean } | null>(null);
  const pendingConflictRef = useRef<{
    method: 'google' | 'apple';
    credential: AuthCredential;
    pendingAnonUid: string;
    email?: string;
  } | null>(null);

  const buildUtmBody = () => {
    const utm = getStoredUtm();
    const meta = getMetaAttribution();
    return {
      ...(utm ? {
        utmSource: utm.utm_source,
        utmMedium: utm.utm_medium,
        utmCampaign: utm.utm_campaign,
        utmTerm: utm.utm_term,
        utmContent: utm.utm_content,
        firstSeenAt: utm.firstSeenAt,
        landingPage: utm.landingPath,
        initialDesign: utm.initialDesign,
      } : {}),
      fbp: meta.fbp,
      fbc: meta.fbc,
      fbclid: meta.fbclid,
    };
  };

  const handleLinkCredentialConflict = async (error: unknown, method: 'google' | 'apple', anonUid: string) => {
    const firebaseError = error as FirebaseError;
    const code = firebaseError?.code;
    if (code !== 'auth/credential-already-in-use' && code !== 'auth/email-already-in-use') {
      throw error;
    }

    const credential =
      method === 'google'
        ? GoogleAuthProvider.credentialFromError(firebaseError)
        : OAuthProvider.credentialFromError(firebaseError);
    if (!credential) {
      logError('OAuth link conflict missing credential', { code, method });
      throw error;
    }

    const email = (firebaseError.customData as { email?: string } | undefined)?.email;
    let hasStorage = false;
    if (email) {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          const check = await checkExistingAccount(token, email);
          hasStorage = check.hasStorage;
        }
      } catch {
        /* non-blocking pre-check */
      }
    }
    pendingConflictRef.current = {
      method,
      credential,
      pendingAnonUid: anonUid,
      email,
    };
    ga.event('existing_account_conflict_shown', { method, has_storage: hasStorage });
    setAccountConflict({ open: true, method, hasStorage });
  };

  const linkWithGoogle = async () => {
    const user = authStore.getSnapshot().user;
    if (!user) return;
    const provider = new GoogleAuthProvider();
    try {
      if (user.isAnonymous) {
        await linkWithPopup(user, provider);
        const token = await auth.currentUser?.getIdToken(true);
        if (token) {
          await upsertAuthUser(token, buildUtmBody());
          ga.event('anon_account_linked', { method: 'google' });
          await fetchUserInfo(token, auth.currentUser!.uid);
        }
      } else {
        authStore.setLastLoginMethod('google');
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      if (user.isAnonymous) {
        try {
          await handleLinkCredentialConflict(error, 'google', user.uid);
        } catch (linkError) {
          const message = linkError instanceof Error ? linkError.message : String(linkError);
          logError('Google link/sign in failed', { error: message, code: (error as FirebaseError)?.code });
          ga.event('auth_failed', { method: 'google', error: message });
        }
        return;
      }
      const message = error instanceof Error ? error.message : String(error);
      logError('Google link/sign in failed', { error: message });
      ga.event('auth_failed', { method: 'google', error: message });
    }
  };

  const linkWithApple = async () => {
    const user = authStore.getSnapshot().user;
    if (!user) return;
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    try {
      if (user.isAnonymous) {
        await linkWithPopup(user, provider);
        const token = await auth.currentUser?.getIdToken(true);
        if (token) {
          await upsertAuthUser(token, buildUtmBody());
          ga.event('anon_account_linked', { method: 'apple' });
          await fetchUserInfo(token, auth.currentUser!.uid);
        }
      } else {
        authStore.setLastLoginMethod('apple');
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      if (user.isAnonymous) {
        try {
          await handleLinkCredentialConflict(error, 'apple', user.uid);
        } catch (linkError) {
          const message = linkError instanceof Error ? linkError.message : String(linkError);
          logError('Apple link/sign in failed', { error: message, code: (error as FirebaseError)?.code });
          ga.event('auth_failed', { method: 'apple', error: message });
        }
        return;
      }
      const message = error instanceof Error ? error.message : String(error);
      logError('Apple link/sign in failed', { error: message });
      ga.event('auth_failed', { method: 'apple', error: message });
    }
  };

  const confirmExistingAccountSignIn = async () => {
    const pending = pendingConflictRef.current;
    if (!pending) return;
    setConflictBusy(true);
    try {
      const anonToken = await auth.currentUser?.getIdToken();
      if (anonToken && pending.email) {
        await prepareGuestAbandon(anonToken, pending.email);
      }
      await signInWithCredential(auth, pending.credential);
      const signedInUser = auth.currentUser;
      if (signedInUser) {
        authStore.patch({ user: signedInUser, isAnonymous: signedInUser.isAnonymous });
      }
      const token = await signedInUser?.getIdToken(true);
      if (token) {
        await abandonAnonymousSession(token, pending.pendingAnonUid);
        ga.event('existing_account_sign_in_chosen', { method: pending.method });
        ga.event('anon_session_abandoned', { method: pending.method });
        authStore.setLastLoginMethod(pending.method);
        authStore.resetLoginLogged();
        await fetchUserInfo(token, auth.currentUser!.uid);
      }
    } catch (error) {
      logError('Failed to sign in to existing account', { error });
    } finally {
      pendingConflictRef.current = null;
      setAccountConflict(null);
      setConflictBusy(false);
    }
  };

  const dismissAccountConflict = () => {
    ga.event('existing_account_conflict_dismissed', { method: accountConflict?.method ?? 'google' });
    pendingConflictRef.current = null;
    setAccountConflict(null);
  };

  const signInAnonymouslyFn = async () => {
    authStore.setLastLoginMethod('anonymous');
    try {
      await signInAnonymously(auth);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logError('Anonymous sign in failed', { error: message });
      ga.event('auth_failed', { method: 'anonymous', error: message });
      throw error;
    }
  };

  const completeCloudChoice = async () => {
    const user = authStore.getSnapshot().user;
    if (!user) return;
    const token = await user.getIdToken(true);
    await completeCloudChoiceApi(token);
    authStore.patch({ needsCloudChoice: false });
    await fetchUserInfo(token, user.uid);
  };

  const signInWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    authStore.setLastLoginMethod('google');
    const eventId = createMetaEventId('InitiateCheckout', `google_${Date.now()}`);
    ga.event('initiate_checkout', { method: 'google', event_id: eventId });
    relayMetaCapiEvent('InitiateCheckout', { method: 'google', eventId });
    signInWithPopup(auth, provider).catch(error => {
      const message = error instanceof Error ? error.message : String(error);
      logError('Google sign in failed', { error: message });
      ga.event('auth_failed', { method: 'google', error: message });
      relayMetaCapiEvent('AuthFailed', { method: 'google', error: message });
    });
  };

  const signInWithApple = () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    authStore.setLastLoginMethod('apple');
    const eventId = createMetaEventId('InitiateCheckout', `apple_${Date.now()}`);
    ga.event('initiate_checkout', { method: 'apple', event_id: eventId });
    relayMetaCapiEvent('InitiateCheckout', { method: 'apple', eventId });
    signInWithPopup(auth, provider).catch(error => {
      const message = error instanceof Error ? error.message : String(error);
      logError('Apple sign in failed', { error: message });
      ga.event('auth_failed', { method: 'apple', error: message });
      relayMetaCapiEvent('AuthFailed', { method: 'apple', error: message });
    });
  };

  useEffect(() => {
    if (!loggingOut) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [loggingOut]);

  const performLogout = async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setLoggingOut(true);
    try {
      const snap = authStore.getSnapshot();
      const isGuest = resolveIsAnonymous(snap);
      if (isGuest && snap.idToken) {
        try {
          await logoutAnonymousSession(snap.idToken);
          ga.event('anon_session_deleted', {});
        } catch (error) {
          logError('Failed to delete guest session on logout', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      // Clear cached data
      localStorage.removeItem('driveLinked');
      localStorage.removeItem('driveEmail');
      localStorage.removeItem('storageProvider');
      localStorage.removeItem('cassette_artists');
      localStorage.removeItem('cassette_releases');
      localStorage.removeItem('dashboard-viewScope');
      localStorage.removeItem('dashboard-sortBy');
      localStorage.removeItem('dashboard:viewMode');

      // Sign out from Firebase; onAuthStateChanged clears the snapshot.
      await firebaseSignOut(auth);

      logInfo('User logged out successfully');
    } catch (error) {
      logError('Error during logout', { error: error instanceof Error ? error.message : String(error) });
    } finally {
      loggingOutRef.current = false;
      setLoggingOut(false);
    }
  };

  const logout = () => performLogout();

  const linkStorage = async (provider: StorageProvider) => {
    const user = authStore.getSnapshot().user;
    if (!user) {
      throw new Error('Not signed in');
    }
    setOauthInProgress(true);
    try {
      await startStorageOAuthRedirect(provider);
    } catch (error) {
      logError('Failed to start storage OAuth redirect', {
        error: error instanceof Error ? error.message : String(error),
        provider,
      });
      setOauthInProgress(false);
      throw error;
    }
  };

  const linkGoogleDrive = () => {
    void linkStorage('gdrive');
  };

  const skipStorage = async () => {
    await skipStorageLink();
    localStorage.setItem('storageProvider', 'cassette_s3');
    localStorage.setItem('driveLinked', 'true');
    authStore.patch({
      usingCassetteCloud: true,
      storageProvider: 'cassette_s3',
      driveLinked: true,
    });
  };

  const disconnectStorageFn = async () => {
    await disconnectStorage();
    localStorage.removeItem('driveLinked');
    localStorage.removeItem('driveEmail');
    authStore.patch({
      driveLinked: false,
      driveEmail: null,
      storageProvider: '',
      usingCassetteCloud: false,
    });
  };

  const unlinkGoogleDrive = disconnectStorageFn;

  const isGDriveTokenExpired = () => false;

  const refreshIdToken = async () => {
    const user = authStore.getSnapshot().user;
    if (user) {
      try {
        const token = await user.getIdToken(true);
        authStore.patch({ idToken: token });
        logInfo('ID token refreshed successfully', { uid: user.uid });
        return token;
      } catch (error) {
        logError('Failed to refresh ID token', { error, uid: user.uid });
        throw error;
      }
    }
    throw new Error('No user to refresh token for');
  };

  const clearUtmAfterPurchase = () => {
    clearStoredUtm();
  };

  const refreshUserData = async () => {
    const { user, idToken } = authStore.getSnapshot();
    if (user && idToken) {
      await fetchUserInfo(idToken, user.uid);
    }
  };

  const setUserType = (type: 'starter' | 'artist' | 'studio') => {
    authStore.patch({ userType: type });
  };

  const applyPromoCode = async (code: string) => {
    try {
      const response = await redeemPromoCode(code);

      if (response.success) {
        authStore.patch({
          userType: 'artist',
          includesCassetteCloud: false,
          eligibleForCassetteProjectStorage: false,
          promoCode: code,
          ...(response.premiumUntil ? { promoExpiry: new Date(response.premiumUntil) } : {}),
        });
        logInfo('Promo code applied successfully', { code });
        return { success: true, message: response.message || 'Promo code applied successfully!' };
      }
      logError('Promo code application failed', { code, error: response.errorMessage });
      return { success: false, message: response.errorMessage || 'Failed to apply promo code' };
    } catch (error) {
      logError('Error applying promo code', { code, error });
      return { success: false, message: 'Failed to apply promo code. Please try again.' };
    }
  };

  // Register storage OAuth expired handler once a user is available.
  useEffect(() => {
    if (snap.user) {
      logInfo('Registering storage OAuth expired handler with unlink callback');
      registerStorageAuthExpiredHandler(disconnectStorageFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap.user]);

  const value: AuthContextType = {
    user: snap.user,
    loading,
    isAnonymous: resolveIsAnonymous(snap),
    needsCloudChoice: snap.needsCloudChoice,
    signInWithGoogle,
    signInWithApple,
    signInAnonymously: signInAnonymouslyFn,
    linkWithGoogle,
    linkWithApple,
    completeCloudChoice,
    logout,
    loggingOut,
    accountConflict,
    confirmExistingAccountSignIn,
    dismissAccountConflict,
    conflictBusy,
    userType: snap.userType,
    setUserType,
    promoCode: snap.promoCode,
    promoExpiry: snap.promoExpiry,
    applyPromoCode,
    driveLinked: snap.driveLinked,
    driveEmail: snap.driveEmail,
    storageLinked:
      isEffectivelyStorageLinked({
        userType: snap.userType,
        includesCassetteCloud: snap.includesCassetteCloud,
        driveLinked: snap.driveLinked,
        storageProvider: snap.storageProvider,
        isAnonymous: resolveIsAnonymous(snap),
      }) && !(snap.storageProvider === 'cassette_s3' && snap.s3Ready === false),
    usingCassetteCloud: snap.usingCassetteCloud,
    includesCassetteCloud: snap.includesCassetteCloud,
    eligibleForCassetteProjectStorage: snap.eligibleForCassetteProjectStorage,
    storageProvider: snap.storageProvider,
    storageEmail: snap.driveEmail,
    migrationStatus: snap.migrationStatus,
    s3Ready: snap.s3Ready,
    linkGoogleDrive,
    linkStorage,
    skipStorage,
    unlinkGoogleDrive,
    disconnectStorage: disconnectStorageFn,
    isGDriveTokenExpired,
    refreshIdToken,
    idToken: snap.idToken,
    oauthInProgress,
    clearUtmAfterPurchase,
    refreshUserData,
    isNewUser: snap.isNewUser,
    clearNewUserSession: () => authStore.patch({ isNewUser: false }),
    firstName: snap.firstName,
    lastName: snap.lastName,
    displayName: snap.displayName,
    defaultTrackId: snap.defaultTrackId,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {loggingOut && (
        <div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 bg-[var(--color-bg)]/90 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <CassetteLoader size={80} variant="inline" />
          <p className="text-sm font-medium text-[var(--color-muted)]">{t('auth.loggingOut')}</p>
        </div>
      )}
    </AuthContext.Provider>
  );
};
